import { EyeOff, Trash2, ArrowRightLeft, Plus, FolderPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SkinInfoSectionProps {
  currentSkinName: string;
  currentSkin: any;
  skuConfig: any;
  addedSelection: any;
  selections: any[];
  templateConfig: any;
  onMoveClick: () => void;
  onRemove: () => void;
  onAddToSelection: (selectionId: string) => void;
}

export function SkinInfoSection({
  currentSkinName,
  currentSkin,
  skuConfig,
  addedSelection,
  selections,
  templateConfig,
  onMoveClick,
  onRemove,
  onAddToSelection,
}: SkinInfoSectionProps) {
  return (
    <>
      {/* Skin Name + Category + Selection */}
      <div className="space-y-1">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground shrink-0">
            skinName:
          </span>
          <div className="text-xs font-mono break-all flex-1">
            {currentSkinName}
          </div>
          {skuConfig.hidden && (
            <Badge variant="destructive" className="gap-1 shrink-0">
              <EyeOff className="h-3 w-3" />
              隐藏
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground shrink-0">
            category:
          </span>
          <Badge variant="outline" className="text-xs font-mono">
            {currentSkin?.category || "-"}
          </Badge>
          {currentSkin?.category &&
            templateConfig?.requireCategories?.includes(
              currentSkin.category,
            ) && (
              <Badge
                variant="default"
                className="text-xs bg-amber-500 hover:bg-amber-600"
              >
                必选
              </Badge>
            )}
        </div>
        {/* Selection 信息 */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground shrink-0">
            selection:
          </span>
          {addedSelection ? (
            <>
              <Badge variant="secondary" className="text-xs">
                {addedSelection.name || addedSelection.id}
              </Badge>
              <div className="flex items-center gap-1 ml-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs gap-1"
                  onClick={onMoveClick}
                >
                  <ArrowRightLeft className="h-3 w-3" />
                  移动
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
                  onClick={onRemove}
                >
                  <Trash2 className="h-3 w-3" />
                  移除
                </Button>
              </div>
            </>
          ) : (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              未添加
            </Badge>
          )}
        </div>
      </div>

      {/* 添加到 Selection（未添加时显示） */}
      {!addedSelection && selections.length > 0 && (
        <div className="p-3 rounded-lg border bg-amber-50 border-amber-200">
          <div className="text-sm font-medium text-amber-800 mb-2">
            添加到 Selection
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full gap-2">
                <Plus className="h-4 w-4" />
                选择 Selection
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="center"
              className="w-48 max-h-64 overflow-y-auto"
            >
              {selections.map((sel: any) => (
                <DropdownMenuItem
                  key={sel.id}
                  onClick={() => onAddToSelection(sel.id)}
                  className="gap-2"
                >
                  <FolderPlus className="h-4 w-4" />
                  {sel.name || sel.id}
                  <Badge variant="secondary" className="ml-auto text-[10px]">
                    {(sel.skuIds || []).length}
                  </Badge>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {!addedSelection && selections.length === 0 && (
        <div className="p-3 rounded-lg border bg-red-50 border-red-200 text-center">
          <p className="text-xs text-red-600">
            请先创建 Selection，然后再添加 Skin
          </p>
        </div>
      )}
    </>
  );
}
